/**
 * Generator registry: maps a TopicId to its generator implementation.
 *
 * Screens and engines look topics up here rather than importing generators
 * directly, so adding a topic is a one-line registration. Topics without a
 * generator yet are simply absent; callers check `hasGenerator` and can render
 * a "coming soon" state, which lets the full curriculum structure exist in the
 * UI while generators are filled in incrementally.
 */

import type { Generator } from "./types";
import type { TopicId } from "../curriculum/curriculum";
import { equivalentForms } from "./equivalent-forms";
import { compareRationals } from "./compare-rationals";
import { orderRationals } from "./order-rationals";
import { understandIntegers } from "./understand-integers";
import { opposites } from "./opposites";
import { absoluteValue } from "./absolute-value";
import { compareOrderIntegers } from "./compare-order-integers";
import { integerOperations } from "./integer-operations";
import { partsExpression } from "./parts-expression";
import { evaluateExpressions } from "./evaluate-expressions";
import { solveEquations } from "./solve-equations";
import { fractionOperations } from "./fraction-operations";
import { decimalOperations } from "./decimal-operations";
import { multiplicativeInverse } from "./multiplicative-inverse";
import { divideFractions } from "./divide-fractions";
import { additiveInverse } from "./additive-inverse";
import { exponentExpressions } from "./exponent-expressions";
import { equivalentExpressions } from "./equivalent-expressions";
import { writeEquations } from "./write-equations";
import { writeInequalities } from "./write-inequalities";
import { solveInequalities } from "./solve-inequalities";
import { independentDependent } from "./independent-dependent";
import { ratiosRates } from "./ratios-rates";
import { convertUnits } from "./convert-units";
import { measuringAngles } from "./measuring-angles";
import { angleRelationships } from "./angle-relationships";
import { area2d } from "./area-2d";
import { areaComposite } from "./area-composite";
import { nets3d } from "./nets-3d";
import { surfaceArea } from "./surface-area";
import { volumePrisms } from "./volume-prisms";
import { coordinatePlane } from "./coordinate-plane";
import { graphingPolygons } from "./graphing-polygons";
import { sampleSize } from "./sample-size";
import { measuresCenterSpread } from "./measures-center-spread";
import { boxPlots } from "./box-plots";
import { dataShape } from "./data-shape";
import { likelihood } from "./likelihood";
import { simpleProbability } from "./simple-probability";
import { complementProbability } from "./complement-probability";

const REGISTRY = new Map<TopicId, Generator>();

function register(gen: Generator): void {
  REGISTRY.set(gen.topic, gen);
}

register(equivalentForms);
register(compareRationals);
register(orderRationals);
register(understandIntegers);
register(opposites);
register(absoluteValue);
register(compareOrderIntegers);
register(integerOperations);
register(partsExpression);
register(evaluateExpressions);
register(solveEquations);
register(fractionOperations);
register(decimalOperations);
register(multiplicativeInverse);
register(divideFractions);
register(additiveInverse);
register(exponentExpressions);
register(equivalentExpressions);
register(writeEquations);
register(writeInequalities);
register(solveInequalities);
register(independentDependent);
register(ratiosRates);
register(convertUnits);
register(measuringAngles);
register(angleRelationships);
register(area2d);
register(areaComposite);
register(nets3d);
register(surfaceArea);
register(volumePrisms);
register(coordinatePlane);
register(graphingPolygons);
register(sampleSize);
register(measuresCenterSpread);
register(boxPlots);
register(dataShape);
register(likelihood);
register(simpleProbability);
register(complementProbability);

export function getGenerator(topic: TopicId): Generator | undefined {
  return REGISTRY.get(topic);
}

export function hasGenerator(topic: TopicId): boolean {
  return REGISTRY.has(topic);
}

export function implementedTopics(): readonly TopicId[] {
  return [...REGISTRY.keys()];
}
